import { useMemo } from 'react';

import { infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query';
import flatMap from 'lodash/flatMap';

import { SortDirection } from '@cast/design-system';
import {
  CostReportWorkloadSummariesResponseWorkloadItem,
  WorkloadCostSummariesResponse,
} from '@cast/types';

import { apiClient } from '@cast/infrastructure-api';

const WORKLOADS_COST_SUMMARIES_QUERY_KEY = 'workloads-cost-summaries';

const getPaginatedWorkloadCostSummariesQueryKey = ({
  clusterId,
  startTime,
  endTime,
  useListingPrices,
  labels,
  namespaces,
  workloadNames,
  workloadTypes,
  pageLimit,
  sortBy,
  sortDirection,
  includeIdleCosts,
}: Partial<PaginatedWorkloadCostSummariesQueryArgs> = {}) =>
  [
    WORKLOADS_COST_SUMMARIES_QUERY_KEY,
    ...(clusterId !== undefined
      ? [
          clusterId,
          startTime,
          endTime,
          useListingPrices,
          labels,
          namespaces,
          workloadNames,
          workloadTypes,
          pageLimit,
          sortBy,
          sortDirection,
          includeIdleCosts,
        ]
      : []),
  ] as const;

export type PaginatedWorkloadCostSummariesQueryArgs = {
  clusterId: string;
  startTime: string;
  endTime: string;
  useListingPrices: boolean;
  labels?: Array<{ label?: string; value?: string }>;
  namespaces?: string[];
  workloadNames?: string[];
  workloadTypes?: string[];
  pageLimit?: number;
  sortBy?: string;
  sortDirection?: SortDirection;
  enabled?: boolean;
  includeIdleCosts?: boolean;
};

const getPaginatedWorkloadCostSummariesQueryOptions = ({
  clusterId,
  startTime,
  endTime,
  useListingPrices,
  includeIdleCosts = false,
  labels,
  namespaces,
  workloadNames,
  workloadTypes,
  pageLimit = 50,
  sortBy,
  sortDirection = 'desc',
  enabled = true,
}: PaginatedWorkloadCostSummariesQueryArgs) =>
  infiniteQueryOptions({
    queryKey: getPaginatedWorkloadCostSummariesQueryKey({
      clusterId,
      startTime,
      endTime,
      useListingPrices,
      labels,
      namespaces,
      workloadNames,
      workloadTypes,
      pageLimit,
      sortBy,
      sortDirection,
      includeIdleCosts,
    }),
    queryFn: async ({ pageParam }) => {
      const { data } = await apiClient.costReport.getWorkloadsCostSummaries({
        clusterId,
        startTime,
        endTime,
        filter: { labels, namespaces, workloadNames, workloadTypes },
        pageLimit: String(pageLimit),
        pageCursor: pageParam,
        sortField: sortBy,
        sortOrder: sortDirection,
        useListingPrices,
        includeIdleResourceCosts: includeIdleCosts,
      });
      return {
        ...data,
        items: data.items?.map((item) => ({
          ...item,
          id: `${item.workloadName}-${item.workloadType}-${item.namespace}`,
        })),
      } as WorkloadCostSummariesResponse;
    },
    initialPageParam: '',
    getNextPageParam: ({ nextCursor }) =>
      nextCursor?.length ? nextCursor : undefined,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    enabled,
  });

export const usePaginatedWorkloadCostSummariesQuery = (
  props: PaginatedWorkloadCostSummariesQueryArgs
) => {
  const {
    isLoading,
    data,
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetching,
  } = useInfiniteQuery(getPaginatedWorkloadCostSummariesQueryOptions(props));

  const workloads = useMemo(
    () =>
      flatMap(
        data?.pages,
        (page) => page.items
      ) as CostReportWorkloadSummariesResponseWorkloadItem[],
    [data]
  );

  const lastPage = data?.pages.at(-1);

  return {
    workloads,
    count: lastPage?.count || 0,
    noDataReason: lastPage?.noDataReason,
    isLoading,
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetching,
  };
};
